import React, { useCallback, useMemo } from 'react';
import { Button, HStack, VStack } from '@chakra-ui/react';

import {
	useFilters,
	updateQueryParams,
	useCalendarSettings,
	useShortcodeSettings,
	useCalendarConfig,
} from '../../../../lib';
import { FilterComponent, LocationFilters, SearchFilter } from '.';
import { __ } from '../../../../lib/i18n';

interface IRenderFiltersProps {
	isVisibleOnView?: boolean;
	isDefaultFiltersOpened?: boolean;
	setAreFiltersApplied?: React.Dispatch<React.SetStateAction<boolean>>;
	onClose?: () => void;
}

export const RenderFilters = ({
	isVisibleOnView,
	isDefaultFiltersOpened = true,
	setAreFiltersApplied,
	onClose,
}: IRenderFiltersProps) => {
	const { events, filters, defaultFilters, setFilters, setFilteredEvents, setSearchTerm, handleApplyFilters } =
		useFilters();
	const { settings } = useCalendarSettings();
	const { settings: shortcodeSettings } = useShortcodeSettings();
	const { instanceId } = useCalendarConfig();

	const handleApply = useCallback(() => {
		handleApplyFilters();
		if (setAreFiltersApplied) {
			setAreFiltersApplied(true);
		}
		if (onClose) {
			onClose();
		}
	}, [setAreFiltersApplied, onClose, handleApplyFilters]);

	const handleResetFilters = useCallback(() => {
		const emptyFilters = {
			selectedEventTypes: [],
			selectedEventTags: [],
			selectedEventLocations: [],
			selectedCountries: [],
			selectedStates: [],
			selectedCities: [],
		};
		setFilters({
			...filters,
			...emptyFilters,
		});
		setFilteredEvents(events);
		setSearchTerm('');
		updateQueryParams('', emptyFilters, instanceId);
		if (setAreFiltersApplied) {
			setAreFiltersApplied(false);
		}
		if (onClose) {
			onClose();
		}
	}, [events, filters, instanceId, setFilters, setFilteredEvents, setSearchTerm, setAreFiltersApplied, onClose]);

	const { enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter } =
		settings;
	const showFilters = shortcodeSettings.filters;

	const filterConfig = useMemo(
		() => [
			{
				filterType: 'EventLocations',
				label: settings.venueLabel,
				prop: 'eventLocations',
				isEnabled: !shortcodeSettings.venue && (showFilters || enableVenueFilter),
			},
			{
				filterType: 'EventTypes',
				label: settings.categoryLabel,
				prop: 'eventTypes',
				isEnabled: !shortcodeSettings.cat && (showFilters || enableCategoryFilter),
			},
			{
				filterType: 'EventTags',
				label: settings.tagLabel,
				prop: 'eventTags',
				isEnabled: !shortcodeSettings.tag && (showFilters || enableTagFilter),
			},
		],
		[
			enableVenueFilter,
			enableCategoryFilter,
			enableTagFilter,
			showFilters,
			shortcodeSettings.cat,
			shortcodeSettings.venue,
			shortcodeSettings.tag,
			settings.venueLabel,
			settings.categoryLabel,
			settings.tagLabel,
		]
	);

	const renderButtons = (
		<HStack w='full' alignItems='baseline' pb={6}>
			<Button
				colorScheme='blue'
				variant='solid'
				size='md'
				borderRadius='md'
				fontSize='xs'
				w='full'
				onClick={handleApply}
			>
				{__('Apply Filters')}
			</Button>
			<Button
				variant='outline'
				colorScheme='red'
				size='md'
				borderRadius='md'
				fontSize='xs'
				w='full'
				onClick={handleResetFilters}
			>
				{__('Reset')}
			</Button>
		</HStack>
	);

	const renderFilterComponents = () =>
		filterConfig
			.filter((filter) => filter.isEnabled)
			.map(({ filterType, label, prop }) => (
				<FilterComponent
					key={filterType}
					filterType={filterType as 'EventLocations' | 'EventTypes' | 'EventTags'}
					label={label}
					defaultOptions={defaultFilters[prop]}
					selectedOptions={filters[`selected${prop.charAt(0).toUpperCase() + prop.slice(1)}`]}
					isDefaultOpened={isDefaultFiltersOpened}
				/>
			));

	const { countries, states, cities } = shortcodeSettings;
	const hasLocationFilters = countries && states && cities;
	return (
		<VStack
			paddingBlock={isVisibleOnView ? 4 : 16}
			paddingInlineStart={3}
			paddingInlineEnd={1}
			height='full'
			justifyContent='space-between'
			w='full'
		>
			<VStack w='full' spacing={4}>
				{(showFilters || enableSearchFilter) && <SearchFilter />}
				{!hasLocationFilters && (showFilters || enableLocationFilter) && <LocationFilters />}
				{renderFilterComponents()}
			</VStack>
			{renderButtons}
		</VStack>
	);
};
